// KeyMaster.js
import React, { createContext, useCallback, useContext, useEffect, useRef, useSyncExternalStore } from 'react';

// naming: TOPIC__THING, alpha sorted
export const KEYCHAIN = {
  LIGHTBOX__MEDIA_GALLERY: Symbol('LIGHTBOX__MEDIA_GALLERY'),
  MODAL__ACCOUNT_OPTIONS: Symbol('MODAL__ACCOUNT_OPTIONS'),
  MODAL__ADD_EMOJI: Symbol('MODAL__ADD_EMOJI'),
  MODAL__CHANNEL_SETTINGS: Symbol('MODAL__CHANNEL_SETTINGS'),
  MODAL__CONFERENCE_TRANSCRIPT: Symbol('MODAL__CONFERENCE_TRANSCRIPT'),
  MODAL__CREATE_WIDGET: Symbol('MODAL__CREATE_WIDGET'),
  MODAL__DEBUG_DUMP: Symbol('MODAL__DEBUG_DUMP'),
  MODAL__DELETE_MESSAGE: Symbol('MODAL__DELETE_MESSAGE'),
  MODAL__DEPLOY_CHANGELOG: Symbol('MODAL__DEPLOY_CHANGELOG'),
  MODAL__REMOVE_WIDGET: Symbol('MODAL__REMOVE_WIDGET'),
  MODAL__DIRECT_MESSAGE_PREFERENCES: Symbol('MODAL__DIRECT_MESSAGE_PREFERENCES'),
  MODAL__ERROR_REPORT: Symbol('MODAL__ERROR_REPORT'),
  MODAL__FULL_MESSAGE: Symbol('MODAL__FULL_MESSAGE'),
  MODAL__IMAGE_REMIX: Symbol('MODAL__IMAGE_REMIX'),
  MODAL__MARKDOWN_ATTACHMENT: Symbol('MODAL__MARKDOWN_ATTACHMENT'),
  MODAL__MEMBERS: Symbol('MODAL__MEMBERS'),
  MODAL__NEW_CHANNEL: Symbol('MODAL__NEW_CHANNEL'),
  MODAL__NEW_MESSAGE: Symbol('MODAL__NEW_MESSAGE'),
  MODAL__NEW_WORKSPACE: Symbol('MODAL__NEW_WORKSPACE'),
  MODAL__SPACE_SETUP: Symbol('MODAL__SPACE_SETUP'),
  MODAL__QR_CODE: Symbol('MODAL__QR_CODE'),
  MODAL__WIDGET_COMPOSER: Symbol('MODAL__WIDGET_COMPOSER'),
  MODAL__WORKSPACE_OPTIONS: Symbol('MODAL__WORKSPACE_OPTIONS'),
  POPOVER__AGENT_CHAT_DROPDOWN: Symbol('POPOVER__AGENT_CHAT_DROPDOWN'),
  POPOVER__CHANNEL_SUGGESTION: Symbol('POPOVER__CHANNEL_SUGGESTION'),
  POPOVER__COMPOSER_EMOJIBAR: Symbol('POPOVER__COMPOSER_EMOJIBAR'),
  POPOVER__CONTEXT_MENU: Symbol('POPOVER__CONTEXT_MENU'),
  POPOVER__EMOJIBAR_CONTEXT_MENU: Symbol('POPOVER__EMOJIBAR_CONTEXT_MENU'),
  POPOVER__EMOJI_SUGGESTION: Symbol('POPOVER__EMOJI_SUGGESTION'),
  POPOVER__FLEXICON_EMOJIBAR: Symbol('POPOVER__FLEXICON_EMOJIBAR'),
  POPOVER__MESSAGE_REACTION_CONTEXT_MENU_EMOJIBAR: Symbol('POPOVER__MESSAGE_REACTION_CONTEXT_MENU_EMOJIBAR'),
  POPOVER__MESSAGE_REACTION_EMOJIBAR: Symbol('POPOVER__MESSAGE_REACTION_EMOJIBAR'),
  POPOVER__PROFILE_SUGGESTION: Symbol('POPOVER__PROFILE_SUGGESTION'),
  POPOVER__SIDEBAR_CONTEXT_MENU: Symbol('POPOVER__SIDEBAR_CONTEXT_MENU'),
  POPOVER__SIDEBAR_SECTION_CONTEXT_MENU: Symbol('POPOVER__SIDEBAR_SECTION_CONTEXT_MENU'),
  POPOVER__THREAD_LIST_CONTEXT_MENU: Symbol('POPOVER__THREAD_LIST_CONTEXT_MENU'),
  POPOVER__WIDGETS_BOARD_MENU: Symbol('POPOVER__WIDGETS_BOARD_MENU'),
  RICHDOWN_COMPOSER__FOCUS_ROUTER: Symbol('RICHDOWN_COMPOSER__FOCUS_ROUTER'),
};

const KeyMasterContext = createContext(null);

export function KeyMasterProvider({ children }) {
  const stackRef = useRef([]);
  const listenersRef = useRef(new Set());

  const emitChange = useCallback(() => {
    listenersRef.current.forEach((listener) => listener());
  }, []);

  const register = useCallback(
    (id) => {
      const nextStack = stackRef.current.filter((x) => x !== id);
      nextStack.push(id);
      stackRef.current = nextStack;
      emitChange();
    },
    [emitChange],
  );

  const unregister = useCallback(
    (id) => {
      const prevStack = stackRef.current;
      const nextStack = prevStack.filter((x) => x !== id);
      if (nextStack.length === prevStack.length) return;
      stackRef.current = nextStack;
      emitChange();
    },
    [emitChange],
  );

  const unlocked = useCallback((id) => {
    const stack = stackRef.current;
    const isUnlocked = stack.length > 0 && stack[stack.length - 1] === id;
    return isUnlocked;
  }, []);

  const subscribe = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const getSnapshot = useCallback(() => stackRef.current, []);

  return (
    <KeyMasterContext.Provider value={{ register, unregister, unlocked, subscribe, getSnapshot }}>{children}</KeyMasterContext.Provider>
  );
}

export function useKeyMaster() {
  const context = useContext(KeyMasterContext);
  if (!context) {
    throw new Error('useKeyMaster must be used within KeyMasterProvider');
  }
  return context;
}

export function useRegisterWithKeyMaster(id) {
  const { register, unregister, unlocked: checkUnlocked } = useKeyMaster();

  useEffect(() => {
    if (!id) return;

    register(id);
    return () => {
      unregister(id);
    };
  }, [id, register, unregister]);

  const unlocked = useCallback(() => {
    if (!id) {
      // This is very noisey but it really shouldn't happen
      throw new Error('unlocked() called without a registered keychain id');
    }
    return checkUnlocked(id);
  }, [checkUnlocked, id]);

  return { unlocked };
}

// Reactively checks whether any of the provided keychain IDs are present in the current KeyMaster stack.
export function useAnyKeychainsRegistered(ids = []) {
  const { subscribe, getSnapshot } = useKeyMaster();
  const stack = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  if (!Array.isArray(ids) || ids.length === 0) return false;
  return ids.some((id) => stack.includes(id));
}
